<template>
  <section
    class="relative min-h-screen bg-gradient-to-br from-white via-blue-50 to-cyan-50 overflow-hidden py-20 px-4"
  >
    <!-- Animated Background -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"
        style="animation: float 6s ease-in-out infinite"
      ></div>
      <div
        class="absolute top-40 right-10 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"
        style="animation: float 8s ease-in-out infinite 1s"
      ></div>
    </div>

    <!-- Content -->
    <div class="relative z-10 max-w-6xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1
          class="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4"
        >
          Attendance Methods
        </h1>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          Choose the attendance marking method that works best for your
          institution
        </p>
      </div>

      <!-- ========== MOBILE TAP METHOD ========== -->
      <div class="space-y-12 mb-24">
        <!-- Title -->
        <div class="text-center mb-12">
          <h2 class="text-4xl font-bold text-gray-900 mb-3">
            📱 Mobile Tap Method
          </h2>
          <p class="text-lg text-gray-600">
            Teacher-assisted attendance marking with instant confirmation
          </p>
        </div>

        <!-- Animation + Steps -->
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <!-- Animation -->
          <div class="relative h-96 flex items-center justify-center">
            <div
              class="relative w-full h-full flex items-center justify-center"
            >
              <!-- Teacher Phone -->
              <div
                class="absolute left-0 top-1/2 transform -translate-y-1/2"
                style="animation: bounce 2s ease-in-out infinite"
              >
                <div
                  class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-1 shadow-2xl w-32 h-64"
                >
                  <div
                    class="bg-white rounded-3xl w-full h-full flex flex-col items-center justify-center"
                  >
                    <div class="text-4xl mb-2">📱</div>
                    <p class="text-xs font-bold text-gray-700 text-center px-2">
                      Teacher's Mobile
                    </p>
                    <div
                      class="mt-4 w-20 h-20 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- Waves -->
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="relative w-40 h-40">
                  <div
                    class="absolute inset-0 border-4 border-cyan-400 rounded-full animate-ping"
                  ></div>
                  <div
                    class="absolute inset-0 border-2 border-blue-300 rounded-full"
                    style="
                      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                    "
                  ></div>
                </div>
              </div>

              <!-- Student Card -->
              <div
                class="absolute right-0 top-1/2 transform translate-y-1/2"
                style="animation: bounce 2s ease-in-out infinite 0.5s"
              >
                <div
                  class="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-1 shadow-2xl w-40 h-24"
                >
                  <div
                    class="bg-white rounded-2xl w-full h-full flex flex-col items-center justify-center"
                  >
                    <p class="text-xs font-bold text-gray-700">Student Card</p>
                    <div class="mt-2 text-2xl">🎓</div>
                    <p class="text-xs text-gray-500 mt-1">NFC/RFID</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Steps -->
          <div class="space-y-6">
            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-xl font-bold"
                >
                  1️⃣
                </div>
                <div>
                  <h3 class="text-lg font-bold text-gray-900 mb-2">
                    Student Approaches Teacher
                  </h3>
                  <p class="text-gray-600">
                    Every student goes to the teacher with their NFC/RFID card
                    or mobile device.
                  </p>
                </div>
              </div>
            </div>

            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-cyan-500"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-xl font-bold"
                >
                  2️⃣
                </div>
                <div>
                  <h3 class="text-lg font-bold text-gray-900 mb-2">
                    Teacher Takes Card & Taps Mobile
                  </h3>
                  <p class="text-gray-600">
                    Teacher taps the student's card against the NFC reader on
                    their phone to mark attendance instantly.
                  </p>
                </div>
              </div>
            </div>

            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-teal-500"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-xl font-bold"
                >
                  3️⃣
                </div>
                <div>
                  <h3 class="text-lg font-bold text-gray-900 mb-2">
                    Instant Confirmation
                  </h3>
                  <p class="text-gray-600">
                    Green checkmark and beep sound confirm successful attendance
                    with timestamp and location.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Benefits -->
        <div class="grid md:grid-cols-3 gap-6 mt-12">
          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">⚡</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Lightning Fast</h3>
            <p class="text-gray-600">Mark attendance in under 1 second.</p>
          </div>

          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">🔒</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Highly Secure</h3>
            <p class="text-gray-600">Encrypted NFC/RFID prevents fraud.</p>
          </div>

          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">📊</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Real-Time Data</h3>
            <p class="text-gray-600">Instant sync to cloud & live reports.</p>
          </div>
        </div>
      </div>

      <!-- ========== SMART READER METHOD ========== -->
      <div class="space-y-12">
        <div class="text-center mb-12">
          <h2 class="text-4xl font-bold text-gray-900 mb-3">
            🔐 Smart Reader + Smart Card Method
          </h2>
          <p class="text-lg text-gray-600">
            Automated attendance at classroom entrances
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-12 items-center">
          <!-- Animation -->
          <div class="relative h-96 flex items-center justify-center">
            <div class="relative w-64 h-80 flex items-center justify-center">
              <!-- 🔵 Circle Waves (NEW) -->
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="relative w-48 h-48">
                  <div
                    class="absolute inset-0 border-4 border-cyan-400 rounded-full animate-ping"
                  ></div>
                  <div
                    class="absolute inset-0 border-2 border-blue-300 rounded-full"
                    style="
                      animation: pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1)
                        infinite;
                    "
                  ></div>
                </div>
              </div>

              <!-- Reader -->
              <div
                class="absolute left-0 bottom-0 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-3 shadow-2xl w-36 h-56 flex flex-col items-center justify-center border-2 border-slate-700"
                style="animation: readerPulse 2s ease-in-out infinite"
              >
                <div
                  class="w-20 h-16 bg-gradient-to-b from-cyan-300 to-cyan-400 rounded-lg flex items-center justify-center shadow-inner border border-cyan-500 mb-3"
                >
                  <div class="text-slate-900 font-bold text-lg">NFC</div>
                </div>
                <div
                  class="flex gap-2 mb-2"
                  style="animation: ledBlink 1s infinite"
                >
                  <div class="w-2 h-2 bg-red-500 rounded-full"></div>
                  <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
                <p class="text-white text-xs font-bold">CLASSROOM READER</p>
              </div>

              <!-- Card -->
              <div
                class="absolute right-0 bottom-10"
                style="animation: cardTapEnhanced 2.5s ease-in-out infinite"
              >
                <div
                  class="w-28 h-44 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl shadow-2xl flex flex-col items-center justify-center border-2 border-blue-400 transform -rotate-12"
                >
                  <div class="text-4xl mb-1">💳</div>
                  <p class="text-white text-xs font-bold">STUDENT ID</p>
                  <p class="text-cyan-100 text-xs mt-1">Smart Card</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Steps -->
          <div class="space-y-6">
            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500"
            >
              <h3 class="text-lg font-bold text-gray-900 mb-2">
                1️⃣ Reader Placed in Every Classroom
              </h3>
              <p class="text-gray-600">
                Smart reader devices are installed at the entrance of every
                classroom as a fixed attendance point.
              </p>
            </div>

            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-cyan-500"
            >
              <h3 class="text-lg font-bold text-gray-900 mb-2">
                2️⃣ Student Taps Card on Reader
              </h3>
              <p class="text-gray-600">
                Each student taps their smart card on the reader when entering
                the classroom to mark attendance.
              </p>
            </div>

            <div
              class="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-teal-500"
            >
              <h3 class="text-lg font-bold text-gray-900 mb-2">
                3️⃣ Attendance Automatically Marked
              </h3>
              <p class="text-gray-600">
                The system verifies identity, records timestamp, and confirms
                with a beep sound instantly.
              </p>
            </div>
          </div>
        </div>

        <!-- Benefits -->
        <div class="grid md:grid-cols-3 gap-6 mt-12">
          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">⏱️</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Time Efficient</h3>
            <p class="text-gray-600">
              Eliminates manual roll calls — saves 5–10 minutes per class.
            </p>
          </div>

          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">🎯</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">100% Accuracy</h3>
            <p class="text-gray-600">
              Removes proxy attendance using verified physical tap.
            </p>
          </div>

          <div
            class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-105 text-center"
          >
            <div class="text-5xl mb-4">📈</div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">
              Detailed Reports
            </h3>
            <p class="text-gray-600">
              Get insightful analytics for better decision-making.
            </p>
          </div>
        </div>

        <!-- Pricing -->
        <div class="mt-16 text-center">
          <h3 class="text-3xl font-bold text-gray-900 mb-6">💰 Pricing</h3>
          <div class="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div
              class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2"
            >
              <h4 class="text-xl font-bold text-gray-900 mb-2">Smart Card</h4>
              <p class="text-gray-600 mb-4">Per Card</p>
              <div
                class="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"
              >
                ₹150
              </div>
            </div>
            <div
              class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2"
            >
              <h4 class="text-xl font-bold text-gray-900 mb-2">
                Smart Reader Device
              </h4>
              <p class="text-gray-600 mb-4">Per Reader</p>
              <div
                class="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"
              >
                ₹1500
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes readerPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes cardTapEnhanced {
    0%,
    100% {
      transform: translateY(0) rotate(-12deg);
    }
    50% {
      transform: translateY(-30px) rotate(-12deg);
    }
  }

  @keyframes ledBlink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
</style>
